<template>
    <section>
        <t-breadcrumbs class="t-breadcrumbs--page" v-bind:items="breadcrumbs"/>

        <jetton-info
            v-bind:address="address"
            v-bind="jettonProps"
            v-bind:imageTA="imageTA"/>
    </section>
</template>

<script>
import { truncateTicker } from '~/helpers';
import { breadcrumbsJsonLd, shortenCrumb } from '~/breadcrumbs.js';
import { getAccountStates, getJettonInfo, fetchOffchainJettonMetadata, getContractTypeByInterfaces } from '~/api/toncenterV3';
import { getLpPoolType } from '~/lpPools';
import JettonInfo from './JettonInfo.vue';

export default {
    props: {
        address: {
            type: String,
            required: true,
        },
        // Declared so the route param doesn't land on the root element as an attribute:
        skeletonHint: {
            type: String,
            default: null,
        },
    },

    computed: {
        // Home > the token list > the token, by name once the metadata is in:
        breadcrumbs() {
            return [
                { name: this.$t('breadcrumbs.home'), to: { name: 'index' } },
                { name: this.$t('header.links.market'), to: { name: 'market' } },
                { name: this.jettonProps.name || this.jettonProps.symbol || shortenCrumb(this.address) },
            ];
        },
    },

    data() {
        return {
            jettonProps: {},
            isLoading: false,
            imageTA: null,
        };
    },

    watch: {
        address() {
            this.jettonProps = {};
            this.loadData();
        },
    },

    created() {
        this.loadData();
    },

    methods: {
        reset() {
            this.jettonProps = {};
        },

        async loadData() {
            this.reset();
            this.isLoading = true;

            try {
                await this.loadMainData();
            } catch (error) {
                console.error('Error loading data:', error);
            } finally {
                this.isLoading = false;
            }
        },

        async loadMainData() {
            try {
                const [accountStates, jettonInfo] = await Promise.all([
                    getAccountStates(this.address),
                    getJettonInfo(this.address),
                ]);

                const lpType = getLpPoolType(accountStates?.accounts?.[0]?.code_hash);

                if (lpType) {
                    await this.$router.replace(this.$localizeRoute({
                        name: 'lp',
                        params: { address: this.address },
                    }));
                    return;
                }

                const { balance } = accountStates.accounts[0];
                const { jetton_masters, metadata, address_book } = jettonInfo;

                let jettonMetadata = metadata[jetton_masters[0]?.address]?.token_info[0];

                // Off-chain fallback. Toncenter sometimes hasn't indexed the
                // off-chain content yet (token_info.valid === false or no
                // name/symbol/image). Fetch the JSON pointed to by
                // jetton_content.uri ourselves. Works for IPFS gateways with
                // permissive CORS; for third-party hosts that block CORS this
                // is best-effort — JettonInfo.vue then backfills from DYOR.
                const offchainUri = jetton_masters[0]?.jetton_content?.uri;
                const needsOffchain = offchainUri && (
                    !jettonMetadata
                    || jettonMetadata.valid === false
                    || (!jettonMetadata.name && !jettonMetadata.symbol && !jettonMetadata.image)
                );

                if (needsOffchain) {
                    try {
                        const offchain = await fetchOffchainJettonMetadata(offchainUri);
                        if (offchain) {
                            const resolveImage = (img) => (typeof img === 'string' && img.startsWith('ipfs://')
                                ? img.replace('ipfs://', 'https://ipfs.io/ipfs/')
                                : img);
                            jettonMetadata = {
                                ...(jettonMetadata || {}),
                                name: jettonMetadata?.name || offchain.name,
                                symbol: jettonMetadata?.symbol || offchain.symbol,
                                image: jettonMetadata?.image || resolveImage(offchain.image),
                                description: jettonMetadata?.description || offchain.description,
                            };
                        }
                    } catch (e) {
                        console.error('Offchain jetton metadata fetch failed:', e);
                    }
                }

                const admin = address_book[jetton_masters[0]?.admin_address]?.user_friendly;
                const total_supply = jetton_masters[0].total_supply;

                // eslint-disable-next-line no-underscore-dangle
                this.imageTA = jettonMetadata?.extra?._image_small || jettonMetadata?.extra?._image_big;

                this.jettonProps = {
                    balance: Number(balance),
                    total_supply: Number(total_supply),
                    image_data: jettonMetadata?.image,
                    name: truncateTicker(jettonMetadata?.name, 26),
                    symbol: truncateTicker(jettonMetadata?.symbol),
                    decimals: Number(jettonMetadata?.extra?.decimals || 9),
                    description: jettonMetadata?.description,
                    admin_address: admin || null,
                    admin_type: getContractTypeByInterfaces(address_book[jetton_masters[0]?.admin_address]?.interfaces),
                    metadata_url: jetton_masters[0]?.jetton_content?.uri || null,
                    jetton_content: jetton_masters[0]?.jetton_content || null,
                    is_mutable: jetton_masters[0]?.mintable,
                };

                const payload = {
                    address: this.address,
                    name: jettonMetadata?.name || jettonMetadata?.symbol,
                    // eslint-disable-next-line no-underscore-dangle
                    image: jettonMetadata?.extra?._image_small || jettonMetadata?.extra?._image_big,
                    type: 'token',
                };
                this.$store.dispatch('presaveAddressbookItem', payload);
            } catch (error) {
                console.error('Error loading data:', error);
            } finally {
                this.isLoading = false;
            }
        },
    },

    metaInfo() {
        return {
            script: [breadcrumbsJsonLd(this, this.breadcrumbs)].filter(Boolean),
        };
    },

    components: {
        JettonInfo,
    },
};

</script>
